import * as React from "react";
import { useState, useMemo, useContext } from "react";
import styled from "styled-components";
import moment from "moment";
import { Trans, useTranslation } from "react-i18next";
import MetricsSearchView from "security-app/components/common/MetricsSearchView";
import investigationsDashboardJson from "security-app/components/Investigations/dashboards/investigations-dashboard.json";
import useSearchVersionCheck from "hooks/useSearchVersionCheck";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import { Spinner } from "components/common";
import { Alert } from "components/bootstrap";
import { widgetDragHandleClass, widgetActionsMenuClass } from "views/components/widgets/Constants";
const StyledSearchContainer = styled.div`
  footer,
  .query-tab-create,
  .query-config-btn,
  .fa-star,
  .react-resizable-handle,
  button:has(.fa-copy),
  button:has(.fa-chevron-down),
  .${widgetDragHandleClass}, .${widgetActionsMenuClass} {
    display: none;
  }

  .react-grid-layout,
  .container-fluid > .row:first-of-type {
    pointer-events: none;
  }
`;
const StyledVersionAlert = styled(Alert)`
  margin-left: 10px;
`;
const DashboardContext = React.createContext(null);
export function DashboardProvider({ children }) {
  const [refreshDasboard, setRefreshDasboard] = useState(false);
  const forceRefreshDashboard = () => {
    setRefreshDasboard(true);
    setTimeout(() => setRefreshDasboard(false), 800);
  };
  const value = useMemo(
    () => ({
      refreshDasboard,
      forceRefreshDashboard
    }),
    [refreshDasboard]
  );
  return /* @__PURE__ */ React.createElement(DashboardContext.Provider, { value }, children);
}
export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  return context || { forceRefreshDashboard: () => {
  } };
};
const DASHBOARD_TIME_RANGE_SECONDS = 7 * 24 * 60 * 60;
const WIDGET_TITLE_KEYS = {
  "Time to First Response (minutes)": "dashboard.widget_titles.time_to_first_response",
  "Dwell Time (minutes)": "dashboard.widget_titles.dwell_time",
  "Time to Resolve (minutes)": "dashboard.widget_titles.time_to_resolve",
  "Time to Detect (minutes)": "dashboard.widget_titles.time_to_detect",
  "Opened Investigations": "dashboard.widget_titles.opened_investigations",
  "Closed Investigations": "dashboard.widget_titles.closed_investigations"
};
const withTranslatedWidgetTitles = (viewJson, t) => ({
  ...viewJson,
  state: Object.fromEntries(
    Object.entries(viewJson.state).map(([queryId, state]) => {
      var _a, _b;
      return [
        queryId,
        {
          ...state,
          titles: {
            ...state.titles,
            widget: Object.fromEntries(
              Object.entries((_b = (_a = state.titles) == null ? void 0 : _a.widget) != null ? _b : {}).map(([widgetId, title]) => [
                widgetId,
                WIDGET_TITLE_KEYS[title] ? t(WIDGET_TITLE_KEYS[title]) : title
              ])
            )
          }
        }
      ];
    })
  )
});
function Dashboard() {
  const { t } = useTranslation("enterprise_security_investigations");
  const { refreshDasboard: refreshDashboard } = useDashboardContext();
  const viewJson = useMemo(() => withTranslatedWidgetTitles(investigationsDashboardJson, t), [t]);
  const { config: searchConfig, isInitialLoading: searchConfigLoading } = useSearchConfiguration();
  const {
    data: usingMetricsCompatibleOpenSearch,
    isLoading: openSearchVersionLoading,
    error: openSearchVersionError
  } = useSearchVersionCheck("opensearch", ">=2.0.0");
  const {
    data: usingMetricsCompatibleDataNode,
    isLoading: dataNodeSearchVersionLoading,
    error: dataNodeSearchVersionError
  } = useSearchVersionCheck("datanode", ">=5.0.0");
  if (openSearchVersionLoading || dataNodeSearchVersionLoading || searchConfigLoading || refreshDashboard)
    return /* @__PURE__ */ React.createElement(Spinner, null);
  if (openSearchVersionError) return /* @__PURE__ */ React.createElement("div", null, t("dashboard.error", { message: openSearchVersionError.message }));
  if (dataNodeSearchVersionError)
    return /* @__PURE__ */ React.createElement("div", null, t("dashboard.error", { message: dataNodeSearchVersionError.message }));
  const queryTimeRangeLimit = moment.duration(searchConfig == null ? void 0 : searchConfig.query_time_range_limit).asSeconds();
  const isTimeRangeLimitTooLow = queryTimeRangeLimit > 0 && queryTimeRangeLimit < DASHBOARD_TIME_RANGE_SECONDS;
  if (isTimeRangeLimitTooLow) {
    return /* @__PURE__ */ React.createElement(StyledVersionAlert, { bsStyle: "warning", className: "tm", title: t("dashboard.unavailable_title") }, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "dashboard.unavailable_description",
        components: { b: /* @__PURE__ */ React.createElement("b", null), em: /* @__PURE__ */ React.createElement("em", null) },
        values: { duration: moment.duration(searchConfig.query_time_range_limit).humanize() }
      }
    )));
  }
  return usingMetricsCompatibleOpenSearch.satisfied || usingMetricsCompatibleDataNode.satisfied ? /* @__PURE__ */ React.createElement(StyledSearchContainer, null, /* @__PURE__ */ React.createElement(MetricsSearchView, { viewJson })) : /* @__PURE__ */ React.createElement(StyledVersionAlert, { bsStyle: "warning", className: "tm", title: t("dashboard.disabled_title") }, /* @__PURE__ */ React.createElement("p", null, t("dashboard.disabled_description")));
}
export default Dashboard;
